import {
  distance2D,
  type SceneDocument,
  type Vec2,
} from "@calcplan/domain";
import { detectRooms } from "@calcplan/geometry";

export type WallEndpoint = "startMm" | "endMm";

export interface WallVertexReference {
  wallId: string;
  endpoint: WallEndpoint;
}

const JOIN_TOLERANCE_MM = 1;
const MIN_WALL_LENGTH_MM = 100;

export function connectedWallVertexReferences(
  scene: SceneDocument,
  vertexMm: Vec2,
  toleranceMm = JOIN_TOLERANCE_MM,
): WallVertexReference[] {
  const references: WallVertexReference[] = [];
  for (const wall of scene.walls) {
    if (distance2D(wall.startMm, vertexMm) <= toleranceMm) {
      references.push({ wallId: wall.id, endpoint: "startMm" });
    }
    if (distance2D(wall.endMm, vertexMm) <= toleranceMm) {
      references.push({ wallId: wall.id, endpoint: "endMm" });
    }
  }
  return references;
}

export function canMoveWallVertex(
  scene: SceneDocument,
  references: WallVertexReference[],
): boolean {
  if (!references.length) return false;
  return references.every((reference) => {
    const wall = scene.walls.find((candidate) => candidate.id === reference.wallId);
    if (!wall || wall.locked) return false;
    return scene.layers.find((layer) => layer.id === wall.layerId)?.locked !== true;
  });
}

export function constrainWallVertexTarget(
  scene: SceneDocument,
  references: WallVertexReference[],
  originMm: Vec2,
  requestedMm: Vec2,
  minimumLengthMm = MIN_WALL_LENGTH_MM,
): Vec2 {
  let target: Vec2 = [
    Number.isFinite(requestedMm[0]) ? requestedMm[0] : originMm[0],
    Number.isFinite(requestedMm[1]) ? requestedMm[1] : originMm[1],
  ];

  // Project the requested point outside every connected wall's minimum-radius
  // circle. Repeating the pass handles corners connected to more than one wall.
  for (let pass = 0; pass < Math.max(2, references.length * 2); pass += 1) {
    for (const reference of references) {
      const wall = scene.walls.find((candidate) => candidate.id === reference.wallId);
      if (!wall) continue;
      const fixed = reference.endpoint === "startMm" ? wall.endMm : wall.startMm;
      const dx = target[0] - fixed[0];
      const dz = target[1] - fixed[1];
      const length = Math.hypot(dx, dz);
      if (length >= minimumLengthMm) continue;
      const fallbackDx = originMm[0] - fixed[0];
      const fallbackDz = originMm[1] - fixed[1];
      const fallbackLength = Math.hypot(fallbackDx, fallbackDz);
      const unitX = length > 1e-6
        ? dx / length
        : fallbackLength > 1e-6
          ? fallbackDx / fallbackLength
          : 1;
      const unitZ = length > 1e-6
        ? dz / length
        : fallbackLength > 1e-6
          ? fallbackDz / fallbackLength
          : 0;
      target = [
        fixed[0] + unitX * minimumLengthMm,
        fixed[1] + unitZ * minimumLengthMm,
      ];
    }
  }
  return target;
}

export function moveWallVertexInScene(
  scene: SceneDocument,
  references: WallVertexReference[],
  targetMm: Vec2,
): boolean {
  if (!canMoveWallVertex(scene, references)) return false;
  const affectedWallIds = new Set(references.map((reference) => reference.wallId));
  for (const reference of references) {
    const wall = scene.walls.find((candidate) => candidate.id === reference.wallId);
    if (!wall) return false;
    wall[reference.endpoint] = [...targetMm];
    wall.revision += 1;
  }
  for (const opening of scene.openings) {
    if (!affectedWallIds.has(opening.wallId)) continue;
    const wall = scene.walls.find((candidate) => candidate.id === opening.wallId);
    if (!wall) continue;
    opening.depthMm = wall.thicknessMm;
    const available = Math.max(0, distance2D(wall.startMm, wall.endMm) - opening.widthMm);
    opening.offsetMm = Math.max(0, Math.min(opening.offsetMm, available));
  }
  return true;
}

export function previewWallVertexMove(
  scene: SceneDocument,
  references: WallVertexReference[],
  targetMm: Vec2,
): SceneDocument {
  const affectedWallIds = new Set(references.map((reference) => reference.wallId));
  const preview: SceneDocument = {
    ...scene,
    walls: scene.walls.map((wall) => affectedWallIds.has(wall.id)
      ? {
          ...wall,
          startMm: [...wall.startMm],
          endMm: [...wall.endMm],
        }
      : wall),
    openings: scene.openings.map((opening) => affectedWallIds.has(opening.wallId)
      ? { ...opening }
      : opening),
  };
  if (moveWallVertexInScene(preview, references, targetMm)) {
    preview.rooms = detectRooms(preview);
  }
  return preview;
}
